<template>
  <div class="w-full flex flex-col rounded-md shadow-md border p-1">
<!-- Primary row — never wraps; only the internal info cluster wraps -->
    <div class="w-full flex items-center flex-nowrap">
<!-- Context menu — leads the row; dropdown opens to the right -->
      <div class="relative flex-shrink-0 px-1 py-0.5 overflow-visible">
        <button type="button" class="bn-icon-only" aria-label="Actions" @click.stop="onToggleMenu && onToggleMenu(equipment)">
          <XIcon v-if="menuOpen" class="w-4 h-4" />
          <MenuAlt4Icon v-else class="w-4 h-4" />
        </button>
        <div v-if="menuOpen" class="absolute left-full top-0 min-w-[16rem] py-1 px-1 rounded shadow-lg z-50 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-600">
          <div class="flex flex-row items-center hover:text-sky cursor-pointer" @click="onView">
            <div class="w-full whitespace-normal truncate p-1 pl-2 text-left">View</div>
          </div>
          <div v-if="equipment.trackingNumber || equipment.packingListId" class="flex flex-row items-center hover:text-sky cursor-pointer" @click="onOpenPackingList">
            <div class="w-full whitespace-normal truncate p-1 pl-2 text-left">Open Packing List</div>
          </div>
<!-- Delete — label carries the blast radius (a kit takes its members, a box takes every piece) since
     ButtonWithConfirm has no message slot; the count is deliberately absent, see deleteLabel. -->
          <div v-if="showDelete" class="flex flex-row items-center w-full p-1">
            <ButtonWithConfirm :buttonText="deleteLabel" :popperContent="deleteHint" :labelOnly="true" :iconComponent="null" buttonClass="bn-menu-item" @confirmed="onDelete" />
          </div>
        </div>
      </div>
<!-- Box number badge -->
      <div v-if="equipment.boxNumber" class="flex h-6 my-0.5 shadow-md rounded-md bg-white dark:bg-sky w-auto min-w-10 max-w-20 items-center justify-center border flex-shrink-0 flex-grow-0 mr-1">
        <div class="w-full px-0.5 text-center truncate font-semibold text-xs whitespace-nowrap">{{ formatBoxNumber(equipment.boxNumber, equipment.boxYear) }}</div>
      </div>
<!-- Information cluster — flex-grow, wraps internally -->
      <div class="flex-grow min-w-0 flex flex-row flex-wrap items-center gap-x-3 gap-y-0.5 px-1">
<!-- Manufacturer — fixed width keeps rows vertically aligned -->
        <div class="w-full sm:w-[55ch] text-left flex-none whitespace-normal break-words p-0.5 line-clamp-2">
          <span class="font-semibold mr-2 inline-block min-w-[30ch]">{{ capitalizeString(equipment.manufacturer) || '—' }}</span>
          <span v-if="equipment.partNumber " class="text-[10px] text-sky mr-1">P/N:</span>
          <span>{{ capitalizeString(equipment.partNumber) }}</span>
        </div>
<!-- Part number — fixed width (matches partNumber maxlength 25), same pattern as manufacturer so rows align -->
        <!--<div class="w-[25ch] flex-grow sm:flex-none whitespace-normal break-words p-0.5 text-left">
          
        </div>-->
<!-- Customer -->
        <div v-if="equipment.clientId?.name" class="w-auto flex-shrink-0 flex flex-row items-center whitespace-normal truncate">
          <span class="truncate text-sky">{{ equipment.clientId.name }}</span>
        </div>
<!-- Project (number · name) — fixed slot width so rows align and PO sits right after it (not pushed away) -->
        <div v-if="projectLabel" class="w-full sm:w-[40ch] flex-none flex flex-row items-center whitespace-normal truncate">
          <span class="truncate">{{ projectLabel }}</span>
        </div>
<!-- Purchase order — shown whenever present (absent for install items with no PO); display-only, guarded uppercase -->
        <div v-if="equipment.purchaseOrder" class="w-auto flex-shrink-0 flex flex-row items-center whitespace-normal truncate">
          <span class="text-[10px] text-sky mr-1">PO:</span><span class="truncate">{{ equipment.purchaseOrder.toUpperCase() }}</span>
        </div>
      </div>
<!-- Chips — disposition (outlier flag) + status; wrap on small screens so they stack instead of squeezing -->
      <div class="hidden w-auto sm:flex flex-row flex-wrap sm:flex-nowrap items-center gap-1 flex-shrink-0 px-1">
<!-- Kit / Multi-piece indicator -->
        <div v-if="kitOrMultiLabel" class="w-auto flex-shrink-0 px-1 text-[10px] font-semibold" :class="kitOrMultiLabel.color">
          {{ kitOrMultiLabel.label }}
        </div>
        <div v-if="showDispositionChip" class="chip-red">{{ dispositionLabel }}</div>
        <div v-if="showStateChip && !hideStateShipment" :class="derivedState.class">{{ derivedState.label }}</div>
      </div>
    </div>
<!-- Shipment Date + Placement (rack over room; multiBox skipped since refs only reflect one sibling) -->
    <div v-if="showShipmentDate || showPlacement" class="w-full flex flex-row flex-wrap items-center gap-x-3 gap-y-0.5 p-1 border-t">
      <div v-if="showShipmentDate && !hideStateShipment" class="whitespace-normal" :class="shipmentDateIsPast ? 'text-red' : 'text-amber'">
        Shipment: {{ format(new Date(equipment.shipmentId.shipmentDate), 'EEE MMM, d, yyyy p') }}
      </div>
      <div v-if="showPlacement" class="whitespace-normal text-indigo">
        <span v-if="equipment.rackId?._id">Rack: {{ equipment.rackId.name }}</span>
        <span v-else>Room: {{ roomLabel(equipment.roomId) }}</span>
      </div>
    </div>
<!-- Chips — disposition (outlier flag) + status; wrap on small screens so they stack instead of squeezing -->
      <div class="w-full justify-end sm:hidden flex flex-row items-center gap-1 flex-shrink-0 p-1">
<!-- Kit / Multi-piece indicator -->
        <div v-if="kitOrMultiLabel" class="w-auto flex-shrink-0 px-1 text-[10px] font-semibold" :class="kitOrMultiLabel.color">
          {{ kitOrMultiLabel.label }}
        </div>
        <div v-if="showDispositionChip" class="chip-red">{{ dispositionLabel }}</div>
        <div v-if="showStateChip && !hideStateShipment" :class="derivedState.class">{{ derivedState.label }}</div>
      </div>
<!-- Packing list status — per-row inline feedback for the legacy fetch path -->
    <div v-if="packingListBusy || packingListMessage" class="w-full text-left text-xs px-1 py-0.5 border-t" :class="packingListBusy ? 'text-sky' : 'text-amber'">
      <span v-if="packingListBusy">Loading packing list…</span>
      <span v-else>{{ packingListMessage }}</span>
    </div>
<!-- Description — clamped and click-to-expand only below sm, where a 200-char description wraps past 3 lines; sm+ wraps in full so the click is inert -->

    <div v-if="equipment.description" class="w-full text-left border-t px-1 pt-1 cursor-pointer sm:cursor-auto" :class="showFullDescription ? 'whitespace-normal' : 'line-clamp-2 sm:line-clamp-none'" @click="showFullDescription = !showFullDescription">
      <span class="mr-1 text-sky">Description:</span>
      {{ equipment.description }}
    </div>
  </div>
</template>

<script>
import { ref, computed, onBeforeUnmount } from 'vue'
import { format, isValid, isPast } from 'date-fns'
import api from '@/api'
import { capitalizeString, camelCaseToLabel, viewOrDownloadFile, formatBoxNumber, roomLabel } from '@/shared'
import { MenuAlt4Icon, XIcon } from '@heroicons/vue/solid'
import ButtonWithConfirm from '@/components/ui/ButtonWithConfirm.vue'

export default {
  components: { MenuAlt4Icon, XIcon, ButtonWithConfirm },
  props: {
    equipment: { type: Object, required: true },
    isPrime: { type: Boolean, default: false },
    // Suppress the state/shipment status chip + shipment-date line (fulfillment pool context).
    hideStateShipment: { type: Boolean, default: false },
    menuOpen: { type: Boolean, default: false },
    onToggleMenu: { type: Function, default: null },
    // Warehouse-manager delete of an accidental scan. Mirrors the trackers' gate (primary org +
    // warehouseQueue >= 2, the tier the route enforces); the backend is the authoritative guard.
    canDelete: { type: Boolean, default: false },
    // Parent-managed day tick; increments at midnight so time-sensitive computeds re-run without per-row timers.
    todayTick: { type: Number, default: 0 }
  },
  emits: ['open', 'openPackingList', 'delete'],
  setup(props, { emit }) {
  // Refs
    const showFullDescription = ref(false)
    const packingListBusy = ref(false)
    const packingListMessage = ref(null)
    let packingListMessageTimer = null

  // Computed
    // Label shape:
    //   primary user:      companyProjectNumber · name
    //   non-primary user:  companyProjectNumber / clientProjectNumber · name  (slash only when clientProjectNumber exists)
    //   falls back gracefully when any of the three pieces is missing.
    const projectLabel = computed(() => {
      const p = props.equipment.projectId
      if (!p?._id) return ''
      const companyNum = p.companyProjectNumber
      const clientNum = p.clientProjectNumber
      const name = p.name
      let label = companyNum || ''
      if (!props.isPrime && clientNum) {
        label = label ? `${label} / ${clientNum}` : clientNum
      }
      if (name) label = label ? `${label} · ${name}` : name
      return label
    })

    const kitOrMultiLabel = computed(() => {
      const e = props.equipment
      if (e.isKit) return { label: 'Kit',    color: 'text-orange' }
      if (e.kitId) return { label: 'In Kit', color: 'text-sky' }
      // Sealed records also carry multiBox:true, so this must stay ahead of the multiBox branch or a
      // sealed carton reads as a loose multi-piece box.
      const qty = e._multiBoxQty > 1 ? ` · ${e._multiBoxQty}` : ''
      if (e.boxSealed) return { label: `Sealed${qty}`, color: 'text-amber' }
      if (e.multiBox) return { label: `Multi-Piece${qty}`, color: 'text-amber' }
      return null
    })

    // Delete gate. Two kinds of exclusion, for two different reasons:
    //   - kit MEMBER (kitId without isKit): deleting one member from a search result orphans the kit.
    //     The container is fine — the backend takes it and every member atomically.
    //   - lastTransferIn: the destination of a transfer is the only live record of that unit, since the
    //     source is tombstoned 'transferred' and cannot be revived.
    // multiBox rows skip the ref checks entirely: the collapsed row is the $first matched sibling, so its
    // refs are unreliable in BOTH directions, and hiding Delete on a box that could be deleted is the bad
    // direction. The backend re-reads every sibling by box identity and adjudicates.
    const showDelete = computed(() => {
      const e = props.equipment
      if (!props.canDelete) return false
      if (e.kitId && !e.isKit) return false
      if (e.lastTransferIn) return false
      if (e.multiBox) return true
      return !e.roomId && !e.rackId && !e.shipmentId && !e.returnDisposition && !e.allocationId
    })

    // ButtonWithConfirm has no message slot, so the label IS the warning. No count: _multiBoxQty is the
    // number of siblings that MATCHED the query, not the pieces in the box, so quoting it would understate
    // a serial-number search. The backend refuses a whole-box delete unless every piece can go.
    const deleteLabel = computed(() => {
      const e = props.equipment
      if (e.isKit) return 'Delete Kit + Members'
      if (e.boxSealed) return 'Delete Sealed Box'
      if (e.multiBox) return 'Delete Entire Box'
      return 'Delete Box'
    })

    const deleteHint = computed(() => {
      const e = props.equipment
      if (e.isKit) return 'Deletes the kit container and every member box'
      if (e.boxSealed) return 'Deletes the sealed carton and all of its units'
      if (e.multiBox) return 'Deletes the whole box — every piece, or none'
      return 'Deletes this box'
    })

    const showShipmentDate = computed(() => {
      const e = props.equipment
      if (e.multiBox) return false
      const d = e?.shipmentId?.shipmentDate
      return !!d && isValid(new Date(d))
    })

    // Placement block below the primary row — rack takes precedence over room.
    // Skipped for multiBox since the collapsed row's refs only reflect one sibling's placement.
    const showPlacement = computed(() => {
      const e = props.equipment
      if (e.multiBox) return false
      return !!(e.rackId?._id || e.roomId?._id)
    })

    const shipmentDateIsPast = computed(() => {
      // Reference todayTick so this re-runs when the parent ticks the day (e.g., overnight).
      void props.todayTick
      return showShipmentDate.value && isPast(new Date(props.equipment.shipmentId.shipmentDate))
    })

    // Operational state derived from ref presence. returnDisposition is rendered as its own
    // chip in the template so it doesn't override the physical-state signal here.
    // equipment.shipmentId is the rack-canonical overlay set in refLookupStages, so racked-and-shipped
    // items read as the shipment state. Shipped (date in past) = red; Shipping (date in future) = orange.
    // Touches props.todayTick so the label/class refreshes at midnight without per-row timers.
    const derivedState = computed(() => {
      const e = props.equipment
      if (e.shipmentId) {
        void props.todayTick
        const d = e.shipmentId.shipmentDate
        const past = !!d && isValid(new Date(d)) && isPast(new Date(d))
        return past
          ? { label: 'Shipped',  class: 'chip-red' }
          : { label: 'Shipping', class: 'chip-orange' }
      }
      if (e.rackId) return { label: 'Racked',    class: 'chip-sky' }
      if (e.roomId) return { label: 'Assigned',  class: 'chip-indigo' }
      return             { label: 'Unassigned', class: 'chip-green' }
    })

    // Status chip hidden on multiBox rows: the backend collapses multiBox siblings via $first,
    // so the displayed refs belong to one sibling and don't represent the whole unit. Open the row
    // for the per-box breakdown. Kit/Multi-Piece label stays since it's a structural fact.
    const showStateChip = computed(() => {
      const e = props.equipment
      if (e.multiBox) return false
      // Disposition + no operational refs = effectively "not available"; the red disposition
      // chip already conveys that, so the redundant "Unassigned" chip would be noise.
      if (e.returnDisposition && !e.roomId?._id && !e.rackId?._id && !e.shipmentId?._id) return false
      return true
    })

    // Show the disposition chip whenever a disposition is set on the row. MultiBox rows
    // still skip it since the collapsed row's $first-sibling disposition doesn't represent
    // the whole unit.
    const showDispositionChip = computed(() => {
      const e = props.equipment
      return !!e.returnDisposition && !e.multiBox
    })

    // RMA is an acronym and should render all-caps; other disposition values read better title-cased.
    const dispositionLabel = computed(() => {
      const d = props.equipment.returnDisposition
      if (!d) return ''
      return d === 'rma' ? capitalizeString(d) : camelCaseToLabel(d)
    })

  // Handlers
    const onView = () => {
      if (props.onToggleMenu) props.onToggleMenu(props.equipment)
      emit('open')
    }

    // Parent owns the request: it holds the results array and the count that both need updating.
    const onDelete = () => {
      if (props.onToggleMenu) props.onToggleMenu(props.equipment)
      emit('delete', props.equipment)
    }

    // Transient inline status — auto-clears so it doesn't linger on the row.
    const flashPackingListMessage = (msg) => {
      packingListMessage.value = msg
      if (packingListMessageTimer) clearTimeout(packingListMessageTimer)
      packingListMessageTimer = setTimeout(() => { packingListMessage.value = null }, 4000)
    }

    // Mongo doc → emit up so parent opens the Modal (single Modal across the list).
    // Legacy → fetch the Drive file by tracking number locally and surface row-scoped UX feedback.
    const onOpenPackingList = async () => {
      if (props.onToggleMenu) props.onToggleMenu(props.equipment)
      const e = props.equipment
      if (e?.packingListId) {
        emit('openPackingList', e)
        return
      }
      const tracking = e?.trackingNumber
      if (!tracking) {
        flashPackingListMessage('No packing list found for this box.')
        return
      }
      packingListBusy.value = true
      packingListMessage.value = null
      try {
        const res = await api.post('logistics/equipment/packingLists/by-tracking', { trackingNumbers: [tracking] }, { noRetry: true })
        const fileId = res.data?.data?.files?.[0]?.id || null
        if (!fileId) {
          flashPackingListMessage(`No packing list found for tracking ${tracking}.`)
          return
        }
        const fileRes = await api.get(`logistics/equipment/packingLists/file/${fileId}`, { responseType: 'blob', noRetry: true })
        const blob = new Blob([fileRes.data], { type: 'application/pdf' })
        viewOrDownloadFile(blob, 'application/pdf', `Packing list ${tracking}`.trim())
      } catch (err) {
        flashPackingListMessage('Failed to open packing list.')
      } finally {
        packingListBusy.value = false
      }
    }

  // Lifecycle
    onBeforeUnmount(() => {
      if (packingListMessageTimer) clearTimeout(packingListMessageTimer)
    })

    return {
      formatBoxNumber,
      roomLabel,
      showFullDescription,
      projectLabel,
      kitOrMultiLabel,
      showShipmentDate,
      shipmentDateIsPast,
      showPlacement,
      derivedState,
      showStateChip,
      showDispositionChip,
      dispositionLabel,
      onView,
      onDelete,
      showDelete,
      deleteLabel,
      deleteHint,
      onOpenPackingList,
      packingListBusy,
      packingListMessage,
      format,
      camelCaseToLabel,
      capitalizeString
    }
  }
}
</script>
